import { useEffect, useState } from "react";
import { useMutation } from "@apollo/client";
import Banner from "@/components/Banner";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import { showSnackbar } from "@/lib/snackbarUtils";
import {
  DELETE_EDUCATION_PROGRAM_LEVEL_MUTATION,
  educationProgramLevelRefetchQueries,
  type DeleteEducationProgramLevelResponse,
  type DeleteEducationProgramLevelVariables
} from "./mutations";

export type EducationProgramLevelToDelete = {
  levelLabel: string;
  levelCode: string;
  startDateIso: string;
};

export default function DeleteEducationProgramLevelDialog({
  open,
  level,
  pgaId,
  onClose
}: {
  open: boolean;
  level: EducationProgramLevelToDelete | null;
  pgaId: string;
  onClose: () => void;
}) {
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    if (open) setDeleteError(null);
  }, [open]);

  const [deleteLevel, { loading }] = useMutation<
    DeleteEducationProgramLevelResponse,
    DeleteEducationProgramLevelVariables
  >(DELETE_EDUCATION_PROGRAM_LEVEL_MUTATION, {
    refetchQueries: educationProgramLevelRefetchQueries(pgaId),
    awaitRefetchQueries: true
  });

  const handleConfirm = async () => {
    if (!level) return;

    setDeleteError(null);

    try {
      const { data } = await deleteLevel({
        variables: {
          input: {
            pgaId,
            levelCode: level.levelCode,
            startDate: level.startDateIso
          }
        }
      });

      if (data?.deleteEducationProgramLevel.success) {
        showSnackbar("Level deleted");
        onClose();
      } else {
        setDeleteError(data?.deleteEducationProgramLevel.message ?? "Unable to delete level");
      }
    } catch (error) {
      setDeleteError(error instanceof Error ? error.message : "Unable to delete level");
    }
  };

  return (
    <Dialog
      open={open}
      headline="Delete Level?"
      onClosed={onClose}
      actions={
        <>
          <Button variant="text" onClick={onClose} disabled={loading} testId="delete-level-cancel">
            Cancel
          </Button>
          <Button
            variant="filled"
            theme="error"
            onClick={handleConfirm}
            disabled={loading || !level}
            testId="delete-level-confirm"
          >
            Delete
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {deleteError && (
          <Banner variant="error" description={deleteError} inline closable={false} hideSecondaryDismiss />
        )}
        <p className="text-body-medium">
          This will remove{" "}
          {level?.levelLabel && level?.levelCode ? (
            <>
              <strong>
                {level.levelLabel} ({level.levelCode})
              </strong>
            </>
          ) : (
            "this level"
          )}{" "}
          and all requirements under this level from this person's PGM program history when this is the only enrollment
          for that level code. This cannot be undone.
        </p>
      </div>
    </Dialog>
  );
}
